import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import Trivia from "./components/Trivia";
import Timer from "./components/Timer";
import Start from "./components/Start";
import { Earned } from "./components/Earned";
import { loadAllQuestionSets, getRandomizedQuestionSet } from './data';
import Credits from "./components/Credits";
import logo from './assets/logo.png';
import publisherlogo from './assets/publisher-logo.png';
import gamelogo from "./assets/game-logo.png";
import { getConfig } from "./config/gameConfig";
import QuestionSet from "./components/QuestionSet";
import { useLanguage } from './contexts/LanguageContext';
import { Complete } from "./components/Complete";
import useSound from 'use-sound';
import mainMusic from './assets/main.mp3';
import changeOn from './assets/change_on.png';
import changeOff from './assets/change_off.png';
import fiftyOn from './assets/5050_on.png';
import fiftyOff from './assets/5050_off.png';
import lifeUsed from './assets/life_used.png';
import lifeUnused from './assets/life_unused.png';
import { trackProgress } from './utils/progressApi';
import DebugOverlay from './components/DebugOverlay';

function App() {
  // Get language data from context
  const { language } = useLanguage();
  //username 
  const [userName, setUserName] = useState(null)
  // Add state for question set selection
  const [questionSet, setQuestionSet] = useState(null);
  // Add state for available question sets (loaded at runtime)
  const [questionSets, setQuestionSets] = useState([]);
  const [questionSetsLoading, setQuestionSetsLoading] = useState(true);
  //Question - setting the current question number
  const [questionNumber, setQuestionNumber] = useState(1);
  //state for setting stop for the game
  const [stop, setStop] = useState(false)
  //state for seeting amount earned
  const [earned, setEarned] = useState("0")
  // state for showing credits screen
  const [showCredits, setShowCredits] = useState(true);
  const [musicStarted, setMusicStarted] = useState(false);
  const [play, { stop: musicStop, sound }] = useSound(mainMusic, { volume: 0.7, loop: false });
  // Add timerActive state
  const [timerActive, setTimerActive] = useState(true);
  // Add timer warning state
  const [timerWarning, setTimerWarning] = useState(false);
  // Lifeline state
  const [fiftyUsed, setFiftyUsed] = useState(false);
  const [fiftyUsedOnce, setFiftyUsedOnce] = useState(false);
  const [changeUsed, setChangeUsed] = useState(false);
  const [changeUsedOnce, setChangeUsedOnce] = useState(false);
  const [extraQuestion, setExtraQuestion] = useState(null);
  const [usedExtraQuestions, setUsedExtraQuestions] = useState([]);

  // Add state for first error tracking
  const [isFirstError, setIsFirstError] = useState(true);
  // Add state for game completion
  const [gameCompleted, setGameCompleted] = useState(false);
  // Add state for tracking chances used
  const [chancesUsed, setChancesUsed] = useState(0);

  // Add state to control showing Complete after Earned
  const [showCompleteAfterEarned, setShowCompleteAfterEarned] = useState(false);

  // Add state for timer reset trigger (used when lifelines are activated)
  const [timerResetTrigger, setTimerResetTrigger] = useState(0);

  // Store the processed question set data
  const [processedSetData, setProcessedSetData] = useState(null);

  // Load available question sets on mount
  useEffect(() => {
    const loadSets = async () => {
      setQuestionSetsLoading(true);
      const sets = await loadAllQuestionSets();
      setQuestionSets(sets);
      setQuestionSetsLoading(false);
    };
    loadSets();
  }, []);

  // Check for name URL parameter and auto-set username if valid
  useEffect(() => {
    if (!showCredits && !userName) {
      // Parse URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const nameParam = urlParams.get('name');
      
      if (nameParam && nameParam.trim() !== '') {
        if (getConfig('debugMode')) {
          console.log('👤 Auto-setting username from URL:', nameParam);
        }
        setUserName(nameParam.trim());
      }
    }
  }, [showCredits, userName]);

  // Check for questionset URL parameter and auto-select if valid
  useEffect(() => {
    if (!questionSetsLoading && questionSets.length > 0 && !questionSet) {
      // Parse URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const questionSetParam = urlParams.get('questionset');
      
      if (questionSetParam) {
        // Check if the parameter matches a valid question set ID
        const validSet = questionSets.find(set => set.id === questionSetParam);
        
        if (validSet) {
          if (getConfig('debugMode')) {
            console.log('📌 Auto-selecting question set from URL:', questionSetParam);
          }
          setQuestionSet(questionSetParam);
        } else {
          if (getConfig('debugMode')) {
            console.warn('⚠️ Invalid question set ID in URL:', questionSetParam);
            console.log('Available IDs:', questionSets.map(s => s.id));
          }
        }
      }
    }
  }, [questionSetsLoading, questionSets, questionSet]);

  // Process question set when selected - randomize questions for new format
  useEffect(() => {
    const processSet = async () => {
      if (questionSet) {
        const randomized = await getRandomizedQuestionSet(questionSet);
        setProcessedSetData(randomized);
        
        // Track game start when question set is selected
        if (userName) {
          await trackProgress({
            username: userName,
            questionset: randomized?.name || questionSet,
            action: 'start'
          });
        }
      } else {
        setProcessedSetData(null);
      }
    };
    processSet();
  }, [questionSet, userName]);

  // Use the processed question set data
  const currentSetData = processedSetData;
  const currentLevels = currentSetData?.levels || 10; // Get levels from JSON
  const currentQuestions = useMemo(() => {
    const questions = currentSetData?.questions || [];
    return questions.slice(0, currentLevels); // Use only the number of questions defined by levels
  }, [currentSetData, currentLevels]);
  const extraQuestions = currentSetData?.extraQuestions;
  
  // Debug: Log extra questions when they're loaded
  useEffect(() => {
    if (extraQuestions && getConfig('debugMode')) {
      console.log('📦 Extra questions loaded:', {
        count: extraQuestions.length,
        questions: extraQuestions
      });
    }
  }, [extraQuestions]);
  //using useMemo hook to hold the money pyramid data (reversed for display)
  const MoneyPyramidData = useMemo(() => {
    if (!currentSetData?.pyramid) return [];
    const pyramid = currentSetData.pyramid.slice(0, currentLevels); // Use only the number of levels defined
    return [...pyramid].reverse();
  }, [currentSetData, currentLevels]);

  // Ref for the app container (for full-screen)
  const appRef = useRef(null);

  // Fullscreen logic
  const handleRequestFullscreenAndPlay = async () => {
    if (getConfig('fullscreenEnabled')) {
      const el = appRef.current;
      if (el && el.requestFullscreen) {
        try {
          await el.requestFullscreen();
        } catch (e) {
          // Ignore errors (e.g., user denied)
        }
      }
    }
    setTimeout(() => {
      setShowCredits(false);
    }, 1000);
  };

  useEffect(() => {
    if (questionSet && currentQuestions.length > 0 && questionNumber > currentQuestions.length) {
      if (getConfig('debugMode')) {
        console.log('✅ Game completed detected! Setting gameCompleted to true');
      }
      setGameCompleted(true);
      setStop(true);
    }
  }, [questionNumber, currentQuestions, questionSet, setGameCompleted, setStop]);

  useEffect(() => {
    // Use the selected questions set length
    if (questionSet && currentQuestions.length > 0) {
      if (currentQuestions.length !== questionNumber) {
        questionNumber > 1 && currentSetData?.pyramid && setEarned(currentSetData.pyramid.find((m) => m.id === questionNumber - 1).amount)
      } else {
        questionNumber > 1 && currentSetData?.pyramid && setEarned(currentSetData.pyramid.find((m) => m.id === questionNumber).amount)
      }
    }
  }, [questionNumber, currentQuestions, currentSetData, questionSet])

  // Start music on first user interaction
  useEffect(() => {
    const startMusic = () => {
      if (!musicStarted) {
        play();
        setMusicStarted(true);
      }
    };
    window.addEventListener('click', startMusic);
    window.addEventListener('keydown', startMusic);
    return () => {
      window.removeEventListener('click', startMusic);
      window.removeEventListener('keydown', startMusic);
    };
  }, [musicStarted, play]);

  // Loop music if it ends
  useEffect(() => {
    if (sound) {
      sound.on('end', () => {
        sound.stop();
        sound.play();
      });
      return () => {
        sound.off('end');
      };
    }
  }, [sound]);

  // Stop music when Trivia starts, resume otherwise
  useEffect(() => {
    if (!showCredits && userName && questionSet) {
      // In Trivia
      musicStop();
    }
  }, [showCredits, userName, questionSet, musicStop]);

  // Play music when Credits screen is shown
  useEffect(() => {
    if (showCredits) {
      play();
      setMusicStarted(true);
    }
  }, [showCredits, play]);

  useEffect(() => {
    if (stop) setTimerActive(false);
  }, [stop]);

  useEffect(() => {
    if (!stop) setTimerActive(true);
  }, [questionNumber, stop]);

  useEffect(() => {
    if (getConfig('debugMode')) {
      console.log('Complete logic check:', {
        questionNumber,
        currentQuestionsLength: currentQuestions.length,
        isFirstError,
        gameCompleted,
        questionSet,
        shouldShowComplete: questionSet && currentQuestions.length > 0 && questionNumber > currentQuestions.length && !gameCompleted
      });
    }
    
    // When the user completes all questions, show Complete screen (for both perfect runs and runs with errors)
    if (questionSet && currentQuestions.length > 0 && questionNumber > currentQuestions.length && !gameCompleted) {
      if (getConfig('debugMode')) {
        const runType = isFirstError ? 'perfect run' : 'with errors';
        console.log(`🎉 Showing Complete screen (${runType})`);
      }
      setGameCompleted(true);
      setStop(false); // Hide Earned
    }
  }, [questionNumber, currentQuestions.length, isFirstError, gameCompleted, questionSet, setGameCompleted]);

  const handlePlayAgain = async () => {
    // Store the current question set ID to re-randomize
    const currentQuestionSetId = questionSet;
    
    setUserName(null);
    setEarned("0");
    setStop(false);
    setQuestionNumber(1);
    setFiftyUsed(false);
    setFiftyUsedOnce(false);
    setChangeUsed(false);
    setChangeUsedOnce(false);
    setExtraQuestion(null);
    setIsFirstError(true);
    setGameCompleted(false);
    setShowCompleteAfterEarned(false);
    setChancesUsed(0);
    setUsedExtraQuestions([]);
    setTimerResetTrigger(0);
    
    // Re-randomize questions by reprocessing the same question set
    if (currentQuestionSetId) {
      const randomized = await getRandomizedQuestionSet(currentQuestionSetId);
      setProcessedSetData(randomized);
    }
  };
  

  // Compute if Earned is being shown due to an error
  const lastCorrectAnswer = questionNumber - 1;
  const wasError = (isFirstError && lastCorrectAnswer < currentLevels) || !isFirstError;

  const handleChangeUsed = () => setChangeUsed(false);

  // Debug logging for render logic
  if (getConfig('debugMode')) {
    console.log('App render state:', {
      gameCompleted,
      showCompleteAfterEarned,
      showCredits,
      userName,
      questionSet,
      questionNumber,
      currentLevels,
      isFirstError
    });
  }

  return <div className="app" ref={appRef}>
    {
      (gameCompleted || showCompleteAfterEarned) ? (
        <Complete 
          userName={userName} 
          earned={earned} 
          handlePlayAgain={handlePlayAgain}
          questionSet={currentSetData?.name || questionSet}
          totalQuestions={currentLevels}
          correctAnswers={Math.min(questionNumber - 1, currentLevels)}
        />
      ) : showCredits ? (
        <Credits onRequestFullscreenAndPlay={handleRequestFullscreenAndPlay} />
      ) : !userName ? (
        <Start setUserName={setUserName} userName={userName} setQuestionSet={setQuestionSet} />
      ) : !questionSet ? (
        questionSetsLoading ? (
          <div className="start">
            <div className="content">
              <div className="dialog">
                <h2>Φόρτωση...</h2>
              </div>
            </div>
          </div>
        ) : (
          <QuestionSet setQuestionSet={setQuestionSet} sound={sound} questionSets={questionSets} />
        )
      ) : (
        <>
        <div className="logo-bar">
                <a href={getConfig('publisherUrl')} target="_blank" rel="noopener noreferrer">
                  <img src={publisherlogo} alt={language.altText.publisherLogo} className="logo-left" />
                </a>
                <a href={getConfig('gameUrl')} target="_blank" rel="noopener noreferrer">
                  <img src={gamelogo} alt={language.altText.gameLogo} className="logo-right" />
                </a>
            </div>
          <div className="leftbar">
          </div>
          <div className="main">
            {
              stop ? <Earned earned={earned} setUserName={setUserName} userName={userName} setStop={setStop} setQuestionNumber={setQuestionNumber} setEarned={setEarned} setShowCredits={setShowCredits} setQuestionSet={setQuestionSet} setFiftyUsed={setFiftyUsed} setChangeUsed={setChangeUsed} isFirstError={isFirstError} setIsFirstError={setIsFirstError} gameCompleted={gameCompleted} setGameCompleted={setGameCompleted} questionNumber={questionNumber} onShowComplete={() => { setQuestionNumber(prev => prev + 1); setShowCompleteAfterEarned(true); }} wasError={wasError} chancesUsed={chancesUsed} setChancesUsed={setChancesUsed} handlePlayAgain={handlePlayAgain} questionSetName={currentSetData?.name || questionSet} totalQuestions={currentLevels} /> : (<>
                <div className="top">
                  <img src={logo} alt={language.altText.mainLogo} className="logo" />
                  <div className={`timer${timerWarning ? ' warning' : ''}`}>
                    <Timer 
                      setStop={setStop} 
                      questionNumber={questionNumber} 
                      timerActive={timerActive} 
                      setTimerWarning={setTimerWarning}
                      userName={userName}
                      questionSet={currentSetData?.name || questionSet}
                      currentQuestion={currentQuestions[questionNumber - 1]}
                      timerDuration={currentSetData?.timerDuration}
                      resetTrigger={timerResetTrigger}
                    />
                  </div>
                </div>
                <div className="bottom">
                  <Trivia
                    userName={userName}
                    setStop={setStop}
                    setQuestionNumber={setQuestionNumber}
                    questionNumber={questionNumber}
                    questions={currentQuestions}
                    setTimerActive={setTimerActive}
                    fiftyUsed={fiftyUsed}
                    changeUsed={changeUsed}
                    setFiftyUsed={setFiftyUsed}
                    questionSet={currentSetData?.name || questionSet}
                    onChangeUsed={handleChangeUsed}
                    extraQuestion={extraQuestion}
                  />
                </div>

              </>)
            }
          </div>
          {/* Lifelines and Pyramid stacked vertically */}
          <div className="pyramid-section">
            {(getConfig('fiftyFiftyEnabled') || getConfig('changeAnswerEnabled')) && (
              <div className="lifelines-bar">
                {getConfig('fiftyFiftyEnabled') && (
                  <img
                    src={fiftyUsedOnce ? fiftyOff : fiftyOn}
                    alt="50-50 Lifeline"
                    className="lifeline-btn"
                    style={{ cursor: fiftyUsedOnce ? 'not-allowed' : 'pointer', marginRight: 10 }}
                    onClick={async () => {
                      if (!fiftyUsedOnce) {
                        setFiftyUsed(true);
                        setFiftyUsedOnce(true);
                        
                        // Reset timer when lifeline is used
                        setTimerResetTrigger(prev => prev + 1);
                        
                        // Track lifeline usage
                        const currentQuestion = currentQuestions[questionNumber - 1];
                        await trackProgress({
                          username: userName,
                          questionset: currentSetData?.name || questionSet,
                          action: 'lifeline',
                          question: `Q${questionNumber}: ${currentQuestion?.question || 'Unknown question'}`,
                          notes: '50-50 lifeline used'
                        });
                      }
                    }}
                  />
                )}
                {getConfig('changeAnswerEnabled') && (
                  <img
                    src={changeUsedOnce ? changeOff : changeOn}
                    alt="Change Answer Lifeline"
                    className="lifeline-btn"
                    style={{ cursor: changeUsedOnce ? 'not-allowed' : 'pointer' }}
                    onClick={async () => {
                      if (getConfig('debugMode')) {
                        console.log('🎯 Change button clicked!', {
                          changeUsedOnce,
                          hasExtraQuestions: !!extraQuestions,
                          extraQuestionsLength: extraQuestions?.length,
                          usedExtraQuestions
                        });
                      }
                      
                      if (!changeUsedOnce && extraQuestions && extraQuestions.length > 0) {
                        const availableQuestions = extraQuestions.filter(q => !usedExtraQuestions.includes(q.id));
                        
                        if (getConfig('debugMode')) {
                          console.log('📋 Available extra questions:', availableQuestions);
                        }
                        
                        if (availableQuestions.length > 0) {
                          const randomIndex = Math.floor(Math.random() * availableQuestions.length);
                          const selectedQuestion = availableQuestions[randomIndex];

                          if (getConfig('debugMode')) {
                            console.log('✅ Selected extra question:', selectedQuestion);
                          }

                          setExtraQuestion(selectedQuestion);
                          setUsedExtraQuestions(prev => [...prev, selectedQuestion.id]);
                          setFiftyUsed(false);
                          setChangeUsed(true);
                          setChangeUsedOnce(true);

                          // Reset timer when lifeline is used
                          setTimerResetTrigger(prev => prev + 1);

                          // Track lifeline usage
                          const currentQuestion = currentQuestions[questionNumber - 1];
                          await trackProgress({
                            username: userName,
                            questionset: currentSetData?.name || questionSet,
                            action: 'lifeline',
                            question: `Q${questionNumber}: ${currentQuestion?.question || 'Unknown question'}`,
                            notes: 'Change question lifeline used'
                          });
                        } else {
                          // Optional: Handle case where all extra questions are used
                          if (getConfig('debugMode')) {
                            console.log("❌ No more extra questions available.");
                          }
                        }
                      } else if (getConfig('debugMode')) {
                        console.log('❌ Cannot use Change lifeline:', {
                          reason: changeUsedOnce ? 'Already used' : !extraQuestions ? 'No extra questions' : 'Extra questions empty',
                          extraQuestions
                        });
                      }
                    }}
                  />
                )}
              </div>
            )}
            <div className="pyramid">
              <div className="pyramid-container">
                <ul className="moneyList">
                  {(() => {
                    // Dynamic section ranges based on currentLevels
                    const totalLevels = currentLevels;
                    let sectionRanges;
                    
                    if (totalLevels === 10) {
                      // For 10 levels (reversed pyramid: index 9 to 0)
                      sectionRanges = [
                        [8, 10],   // first 2 questions (lowest amounts, indices 8,9)
                        [5, 8],    // next 3 questions (indices 5,6,7)
                        [1, 5],    // next 4 questions (indices 1,2,3,4)
                        [0, 1],    // last 1 question (highest amount, index 0)
                      ];
                    } else {
                      // Generic distribution for other level counts
                      const section1 = Math.ceil(totalLevels * 0.2); // 20%
                      const section2 = Math.ceil(totalLevels * 0.3); // 30%
                      const section3 = Math.ceil(totalLevels * 0.4); // 40%
                      const section4 = totalLevels - section1 - section2 - section3; // remaining
                      
                      sectionRanges = [
                        [totalLevels - section1, totalLevels],
                        [totalLevels - section1 - section2, totalLevels - section1],
                        [section4, totalLevels - section1 - section2],
                        [0, section4],
                      ];
                    }
                    
                    const colors = getConfig('pyramidSectionColors');
                    let items = [];
                    MoneyPyramidData.forEach((money, idx) => {
                      // Find which section this index belongs to
                      let sectionIdx = sectionRanges.findIndex(([start, end]) => idx >= start && idx < end);
                      items.push(
                        <li
                          key={money.id}
                          className={
                            `moneyListItem sectionColor${sectionIdx + 1}` +
                            (questionNumber === money.id ? " active" : "")
                          }
                          style={{ color: colors[sectionIdx] }}
                        >
                          <span className="moneyListItemNumber">{money.id}</span>
                          <span className="moneyListItemAmount">{money.amount}</span>
                        </li>
                      );
                    });
                    return items;
                  })()}
                </ul>
              </div>
            </div>
            {getConfig('secondChances') > 0 && (
              <div className="life-bar">
                {Array.from({ length: getConfig('secondChances') }).map((_, index) => (
                  <img
                    key={index}
                    src={index < chancesUsed ? lifeUsed : lifeUnused}
                    alt={index < chancesUsed ? "Life Used" : "Life Remaining"}
                    className="life-icon"
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )
    }

    {/* Debug overlay - only shows when debugMode is true */}
    <DebugOverlay />
  </div>;
}

export default App;
